<template>
  <div v-if="model">
    <div class="overview-header">
      <h2 style="text-align: center;">{{ title }} {{ chamber | capitalize }}</h2>
      <div class="flex-center" style="margin-bottom: 5px; margin-left: 10px;">
        <div class="race-state" :style="'background-color: ' + getColor" v-html="getRaceState"></div>
        <template v-if="potentialFlip && model.potentialFlip !== 0">
          <div class="flip-state" :style="'background: ' + potentialFlip.gradient">
            {{ potentialFlip.text }}
          </div>
        </template>
      </div>
      <!-- <div class="flex-center">
        <div class="race-prob">{{ winProb | votePercent | winPerc }} </div>
        <div class="race-description">GOP Win Probability</div>
      </div> -->
      <!-- <div class="footnote">These figures represent the current probability of a Republican victory in the race, not a predicted margin for the race</div> -->
      <v-divider style="margin: 10px auto; width: 30%;" v-if="mobile"></v-divider>
    </div>
    <v-divider style="margin: 5px 0;" v-if="!mobile"></v-divider>
    <div>
      <div class="overview-details">
        <p class="overview-detail" style="margin-left: 0px;">
          <span class="overview-label">Current Party:</span>
          <span class="overview-value" :style="partyColor(election.previousParty)">
            {{ election.previousParty | partyAbbrev }}
          </span>
        </p>
        <p class="overview-detail">
          <span class="overview-label">Contested:</span>
          <span class="overview-value">
            <i class="boolean-icons material-icons" style="color: #57AEA6;" v-if="!election.uncontested">done</i>
            <i class="boolean-icons material-icons" style="color: #FA3138;" v-else>clear</i>
          </span>
        </p>
        <p class="overview-detail">
          <span class="overview-label">Open Seat:</span>
          <span class="overview-value">
            <i class="boolean-icons material-icons" style="color: #57AEA6;" v-if="election.openSeat">done</i>
            <i class="boolean-icons material-icons" style="color: #FA3138;" v-else>clear</i>
          </span>
        </p>
        <p class="overview-detail" v-if="pvi">
          <span class="overview-label">PVI:</span>
          <span class="overview-value">
            <a :style="pviColor" href="https://www.cookpolitical.com/pvi-map-and-district-list" v-if="chamber === 'house'">{{ pvi.split('.')[0] }}</a>
            <a :style="pviColor" href="https://www.cookpolitical.com/pvi-0" v-if="chamber === 'senate'">{{ pvi.split('.')[0] }}</a>
          </span>
        </p>
        <p class="overview-detail" v-if="election.turnoutCount">
          <span class="overview-label">Turnout Count:</span>
          <span class="overview-value">{{ election.turnoutCount | commas }}</span>
        </p>
      </div>
    </div>
    <div :style="mobile ? 'text-align: center; margin-top: 20px;' : 'text-align: center;'">
      <span v-for="(item, index) in currentElectionData.filter(item => item.party.includes('Republican'))" :key="item.candidate_name">
        <span style="color: #FA3138; font-weight: 600; font-size: 1em;">{{ item.candidate_name }}</span>
        <span style="font-size: 0.9em;" v-if="item.candidacy_type === 'Incumbent'">(inc)</span>
        <span v-if="index + 1 < currentElectionData.filter(item => item.party.includes('Republican')).length"> | </span>
      </span>
      <span
        v-if="currentElectionData.filter(item => item.party.includes('Democratic')).length > 0 && currentElectionData.filter(item => item.party.includes('Republican')).length > 0"
        style="font-weight: 600; margin: 0 5px;"
        >vs
      </span>
      <span v-for="(item, index) in currentElectionData.filter(item => item.party.includes('Democratic'))" :key="item.candidate_name">
        <span style="color: #66ABCF; font-weight: 600; font-size: 1em;">{{ item.candidate_name }}</span>
        <span style="font-size: 0.9em;" v-if="item.candidacy_type === 'Incumbent'">(inc)</span>
        <span v-if="index + 1 < currentElectionData.filter(item => item.party.includes('Democratic')).length"> | </span>
      </span>
      <div style="text-align: center; font-size: 0.85em; max-width: 300px; margin: 0 auto;">
        <div v-if="currentElectionData.filter(item => !item.party.includes('Democratic') && !item.party.includes('Republican')).length > 0">
          <b style="font-weight: 400;">Other: </b>
          <span style="font-weight: 400; font-size: 0.85em; letter-spacing: 0.03em;" v-for="(item, index) in currentElectionData.filter(item => !item.party.includes('Democratic') && !item.party.includes('Republican'))" :key="'other-' + index">
            {{ item.candidate_name }}
            <span v-if="item.candidacy_type === 'Incumbent'"> (inc)</span>
            <span v-if="index + 1 < currentElectionData.filter(item => !item.party.includes('Democratic') && !item.party.includes('Republican')).length"> | </span>
          </span>
        </div>
      </div>
    </div>
    <div class="share-btns" v-if="mobile">
      <p class="clearfix" style="clear: both; margin-top: 2rem; margin-bottom: 0;">
        <share-buttons
          :twitterMessage="shareTitle + ' - ' + shareMessage"
          :emailSubject="shareTitle"
          :emailMessage="shareMessage">
        </share-buttons>
      </p>
    </div>
    <v-divider style="margin: 40px 0; width: 30%; margin: 20px auto;" v-if="mobile"></v-divider>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Utils from '@/utils'
import { OptiCard } from '@/components/optimus'
import ShareButtons from '@/components/common/ShareButtons'

const props = {
  election: {
    type: Object,
    required: true
  },
  model: {
    type: Object,
    required: false
  },
  chamber: {
    type: String,
    required: false
  }
}

const computed = {
  ...mapGetters({
    currentDate: 'aggregateModels/currentDate',
    electionModels: 'electionModels/list',
    mobile: 'ux/mobile'
  }),
  // Really anoying to have to copy all this shit just for share buttons, but w/e
  models () {
    return this.electionModels.filter(model => model.election.raceId === this.raceId)
  },
  currentModel () {
    return this.models.find(model => model.date === this.currentDate)
  },
  raceId () {
    return this.$route.params.raceId
  },
  categoryBulletPoints () {
    return this.currentModel.categoryBulletPoints
  },
  type () {
    if (this.$route.path.includes('house') === true) {
      return 'house'
    } else if (this.$route.path.includes('senate') === true) {
      return 'senate'
    }
  },
  title () {
    return this.type === 'house' ? `${this.election.state} ${this.election.cd}` : `${this.election.state}`
  },
  shareTitle () {
    return this.type === 'house' ? `House Seat: ${this.title}` : `Senate Seat: ${this.title}`
  },
  shareMessage () {
    if (this.categoryBulletPoints.overview && this.categoryBulletPoints.overview.length > 1) {
      return this.categoryBulletPoints.overview[1]
    }
  },
  incumbent () {
    const incumbent = this.currentElectionData.find(item => item.candidacy_type === 'Incumbent')
    if (incumbent) {
      return incumbent.candidate_name
    }
  },
  electionJson () {
    return JSON.parse(this.election.electionJson)
  },
  currentElectionData () {
    const election = this.electionJson.current_election
    return Object.values(election).filter(item => item.candidate_name)
  },
  winProb () {
    return this.model.gopWinProbability
  },
  getRaceState () {
    const winProb = (this.winProb * 100).toFixed(2)
    const obj = Utils.getColorScale(winProb)
    return `<span style="color: ${obj.textColor};">${obj.state}</span>`
  },
  getColor () {
    const winProb = (this.winProb * 100).toFixed(2)
    const obj = Utils.getColorScale(winProb)
    return obj.color
  },
  potentialFlip () {
    return Utils.getFlip(this.model.potentialFlip)
  },
  pvi () {
    return JSON.parse(this.election.electionJson).pvi
  },
  pviColor () {
    if (this.pvi.includes('D')) {
      return 'color: #66ABCF; font-weight: 600; text-decoration: underline;'
    } else if (this.pvi.includes('R')) {
      return 'color: #FA3138; font-weight: 600; text-decoration: underline;'
    } else {
      return 'color: #854C79; font-weight: 600; text-decoration: underline;'
    }
  }
}

const methods = {
  partyColor (party) {
    if (party === 'Democratic') {
      return 'color: #66ABCF; font-weight: 600;'
    } else if (party === 'Republican') {
      return 'color: #FA3138; font-weight: 600;'
    } else {
      return 'color: #57AEA6; font-weight: 600;'
    }
  }
}

const components = {
  OptiCard,
  ShareButtons
}

export default {
  props,
  computed,
  methods,
  components
}
</script>

<style scoped>
.overview-header {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.overview-details {
  text-align: center;
  margin-bottom: 10px;
}

.overview-detail {
  display: inline-block;
  margin: 0 10px;
}

.overview-label {
  font-weight: 500;
  font-size: 0.8em;
  letter-spacing: 0.02em;
}

.overview-value {
  font-weight: 600;
  font-size: 0.9em;
  margin-left: 2px;
}

.boolean-icons {
  font-size: 1em;
  font-weight: 700;
}

.race-prob {
  font-size: 1.4em;
  font-weight: 700;
}

.flip-state {
  display: inline-block;
  font-size: 0.8em;
  font-weight: 500;
  margin: 0 3px;
  text-transform: uppercase;
  padding: 5px 10px;
  color: white;
  border-radius: 50px;
}

.race-state {
  font-size: 0.8em;
  font-weight: 500;
  margin: 0 3px;
  text-transform: uppercase;
  padding: 5px 10px;
  border-radius: 50px;
}

.race-description {
  font-size: 1em;
  font-weight: 400;
  letter-spacing: 0.02em;
  margin-left: 7px;
}

.desktop-share-btns .clearfix {
  margin-top: 0px !important;
}

@media only screen and (max-width: 960px) {
  .overview-header {
    display: block;
  }

  .overview-details {
    width: 100%;
    text-align: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/detail/Overview.vue